import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  Loader2,
  Phone,
  Image,
  Type,
  ToggleLeft,
  ToggleRight,
  MousePointerClick,
  Save,
  Eye,
  EyeOff,
} from 'lucide-react';
import { getHeaderConfig, saveHeaderConfig, type HeaderConfig, type CTAButton, type NavButtonStyle } from '@/lib/cms';

interface AdminHeaderConfigProps {
  currentUserId: string;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  /** Optional action (e.g. Help & guides) to show in the title row. */
  titleAction?: React.ReactNode;
}

const newButtonId = () => `btn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const emptyButton = (): CTAButton => ({
  id: newButtonId(),
  label: '',
  url: '',
  action: null,
  style: 'primary',
  order: 0,
  isVisible: true,
});

export const AdminHeaderConfig = ({
  currentUserId,
  showNotify,
  titleAction,
}: AdminHeaderConfigProps) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state — mirrors HeaderConfig
  const [logoUrl, setLogoUrl] = useState('');
  const [tagline, setTagline] = useState('');
  const [showContactPhone, setShowContactPhone] = useState(true);
  const [contactPhone, setContactPhone] = useState('');
  const [showProfileButton, setShowProfileButton] = useState(true);
  const [showAdminLink, setShowAdminLink] = useState(true);
  const [ctaButtons, setCtaButtons] = useState<CTAButton[]>([]);

  // Button editing
  const [editingBtn, setEditingBtn] = useState<CTAButton | null>(null);
  const [btnFormOpen, setBtnFormOpen] = useState(false);
  const [btnForm, setBtnForm] = useState<CTAButton>(emptyButton());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await getHeaderConfig();
      if (cfg) {
        setLogoUrl(cfg.logoUrl ?? '');
        setTagline(cfg.tagline ?? '');
        setShowContactPhone(cfg.showContactPhone);
        setContactPhone(cfg.contactPhone ?? '');
        setShowProfileButton(cfg.showProfileButton);
        setShowAdminLink(cfg.showAdminLink);
        setCtaButtons([...cfg.ctaButtons].sort((a, b) => a.order - b.order));
      }
    } catch (err) {
      console.error('[AdminHeaderConfig] Failed to load header config:', err);
      showNotify?.('Failed to load header configuration', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const cfg: Omit<HeaderConfig, 'updatedAt' | 'updatedBy'> = {
        logoUrl: logoUrl.trim() || null,
        tagline: tagline.trim() || null,
        showContactPhone,
        contactPhone: contactPhone.trim() || null,
        showProfileButton,
        showAdminLink,
        ctaButtons: ctaButtons.map((b, i) => ({ ...b, order: i })),
      };
      await saveHeaderConfig(cfg, currentUserId);
      showNotify?.('Header configuration saved', 'success');
    } catch (err) {
      console.error('[AdminHeaderConfig] Failed to save header config:', err);
      showNotify?.('Failed to save header configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── CTA Buttons ────────────────────────────────────────────────────────────
  const openAddBtn = () => {
    setEditingBtn(null);
    setBtnForm({ ...emptyButton(), order: ctaButtons.length });
    setBtnFormOpen(true);
  };

  const openEditBtn = (btn: CTAButton) => {
    setEditingBtn(btn);
    setBtnForm({ ...btn });
    setBtnFormOpen(true);
  };

  const handleBtnSave = () => {
    if (!btnForm.label.trim()) {
      showNotify?.('Button label is required', 'error');
      return;
    }
    if (editingBtn) {
      setCtaButtons((prev) => prev.map((b) => (b.id === editingBtn.id ? { ...btnForm } : b)));
    } else {
      setCtaButtons((prev) => [...prev, { ...btnForm, id: newButtonId(), order: prev.length }]);
    }
    setBtnFormOpen(false);
  };

  const deleteBtn = (id: string) => {
    setCtaButtons((prev) => prev.filter((b) => b.id !== id).map((b, i) => ({ ...b, order: i })));
  };

  const moveBtn = (id: string, direction: 'up' | 'down') => {
    setCtaButtons((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((b) => b.id === id);
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= sorted.length) return prev;
      [sorted[idx], sorted[swapIdx]] = [sorted[swapIdx], sorted[idx]];
      return sorted.map((b, i) => ({ ...b, order: i }));
    });
  };

  const toggleBtnVisibility = (id: string) => {
    setCtaButtons((prev) => prev.map((b) => (b.id === id ? { ...b, isVisible: !b.isVisible } : b)));
  };

  // ── Toggle helpers ────────────────────────────────────────────────────────
  const Toggle = ({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) => (
    <button
      type="button"
      onClick={onChange}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${checked ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
    >
      {checked ? <ToggleRight size={18} className="text-blue-600" /> : <ToggleLeft size={18} className="text-gray-400" />}
      {label}
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Header Configuration</h1>
          <p className="text-sm text-gray-500 mt-0.5">Control logo, tagline, contact phone, CTA buttons, and visibility toggles for the site header.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {titleAction}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60 shadow-sm"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save
          </button>
        </div>
      </div>

      {/* Branding */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
          <Image size={16} className="text-blue-500" />
          Branding
        </h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Custom logo URL</label>
          <input
            type="text"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://... (leave blank to use default logo)"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">If left blank, the default BrandLogo component is used.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
            <Type size={13} />
            Tagline
          </label>
          <input
            type="text"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="Government contracting made simple"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">Shown below the logo on desktop. Overrides the branding tagline in the header only.</p>
        </div>
      </section>

      {/* Contact phone */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
          <Phone size={16} className="text-blue-500" />
          Contact Phone
        </h2>
        <div className="flex items-center gap-3">
          <Toggle checked={showContactPhone} onChange={() => setShowContactPhone((v) => !v)} label="Show phone number" />
        </div>
        {showContactPhone && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone number</label>
            <input
              type="text"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+1 832-542-6260"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Overrides the contact phone from Settings &amp; Branding in the header only. Leave blank to fall back to global contact settings.</p>
          </div>
        )}
      </section>

      {/* CTA Buttons */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
            <MousePointerClick size={16} className="text-blue-500" />
            CTA Buttons
          </h2>
          <button
            type="button"
            onClick={openAddBtn}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
          >
            <Plus size={14} />
            Add button
          </button>
        </div>
        {ctaButtons.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No CTA buttons. Add one above.</p>
        ) : (
          <div className="space-y-2">
            {[...ctaButtons].sort((a, b) => a.order - b.order).map((btn, idx) => (
              <div key={btn.id} className={`flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50 ${!btn.isVisible ? 'opacity-50' : ''}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-800">{btn.label}</span>
                  {btn.url && <span className="text-xs text-gray-500 block truncate">{btn.url}</span>}
                  {btn.action === 'open-strategy-call' && <span className="text-xs text-blue-600 block">→ Opens Strategy Call modal</span>}
                </div>
                <span className={`hidden sm:inline-flex px-2 py-0.5 rounded text-xs font-medium ${btn.style === 'primary' ? 'bg-blue-100 text-blue-700' : btn.style === 'secondary' ? 'bg-gray-200 text-gray-700' : 'bg-white border border-gray-300 text-gray-700'}`}>
                  {btn.style}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => moveBtn(btn.id, 'up')} disabled={idx === 0} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-30"><ChevronUp size={14} /></button>
                  <button type="button" onClick={() => moveBtn(btn.id, 'down')} disabled={idx === ctaButtons.length - 1} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-30"><ChevronDown size={14} /></button>
                  <button type="button" onClick={() => toggleBtnVisibility(btn.id)} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200" title={btn.isVisible ? 'Hide' : 'Show'}>{btn.isVisible ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                  <button type="button" onClick={() => openEditBtn(btn)} className="p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50"><Pencil size={14} /></button>
                  <button type="button" onClick={() => deleteBtn(btn.id)} className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Visibility toggles */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
          <ToggleRight size={16} className="text-blue-500" />
          Visibility Toggles
        </h2>
        <div className="flex flex-wrap gap-3">
          <Toggle checked={showProfileButton} onChange={() => setShowProfileButton((v) => !v)} label="Show Profile / Sign-in button" />
          <Toggle checked={showAdminLink} onChange={() => setShowAdminLink((v) => !v)} label="Show Admin link (admins only)" />
        </div>
        <p className="text-xs text-gray-500">These toggles only hide or show the respective elements — authentication is not affected.</p>
      </section>

      {/* Save bar */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60 shadow-sm"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save header configuration
        </button>
      </div>

      {/* Button form modal */}
      {btnFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-blue-600 px-5 py-4">
              <h3 className="text-base font-bold text-white">{editingBtn ? 'Edit Button' : 'Add CTA Button'}</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
                <input
                  type="text"
                  value={btnForm.label}
                  onChange={(e) => setBtnForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="Book a Strategy Call"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action / URL</label>
                <select
                  value={btnForm.action ?? 'external'}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBtnForm((f) => ({
                      ...f,
                      action: val === 'open-strategy-call' ? 'open-strategy-call' : val === 'internal' ? 'internal' : 'external',
                      url: val === 'open-strategy-call' ? '' : f.url,
                    }));
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                >
                  <option value="open-strategy-call">Open Strategy Call modal</option>
                  <option value="internal">Internal page (slug)</option>
                  <option value="external">External URL</option>
                </select>
                {btnForm.action !== 'open-strategy-call' && (
                  <input
                    type="text"
                    value={btnForm.url ?? ''}
                    onChange={(e) => setBtnForm((f) => ({ ...f, url: e.target.value }))}
                    placeholder={btnForm.action === 'internal' ? '/about-us' : 'https://...'}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Style</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['primary', 'secondary', 'outline'] as NavButtonStyle[]).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setBtnForm((f) => ({ ...f, style: s }))}
                      className={`py-2 rounded-lg border text-sm font-medium capitalize transition-colors ${btnForm.style === s ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-blue-200'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="btn-visible"
                  type="checkbox"
                  checked={btnForm.isVisible}
                  onChange={(e) => setBtnForm((f) => ({ ...f, isVisible: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="btn-visible" className="text-sm text-gray-700">Visible on site</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
              <button type="button" onClick={() => setBtnFormOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button type="button" onClick={handleBtnSave} className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                {editingBtn ? 'Save changes' : 'Add button'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
